/**
 * Formations Donoor (donoor.org/formations) — STRUCTURE des parcours.
 *
 * Modèle repris de info.helloasso.com/formations (décision Karim 2026-10-18) :
 * UNE FORMATION PAR FONCTIONNALITÉ (cagnotte, QR codes, billetterie…), chacune
 * illustrée par une image qui montre tout de suite de quoi elle parle, et
 * découpée en modules ; chaque module = une vidéo tuto courte + les
 * étapes écrites + des liens « pour aller plus loin ». Chaque formation propose
 * aussi des fiches pratiques PDF et une FAQ.
 *
 * Ce fichier ne contient AUCUN texte affiché : seulement l'ordre, les
 * identifiants, les vidéos et les images. Les textes vivent dans
 * `textes/<langue>/<slug>.json` (un fichier par formation et par langue, chargé
 * seulement quand on ouvre la formation) et `fiches/<langue>/<id>.json`.
 * Le test `tests/unit/formations-catalogue.test.ts` vérifie que chaque langue a
 * exactement les mêmes modules, fiches et liens que le français.
 *
 * Une vidéo (TUTORIELS) peut servir dans plusieurs formations. Elle n'apparaît sur le site qu'une fois publiée par
 * `node scripts/formations-videos.mjs <id>` (→ public/videos/formations/ +
 * manifeste `videos.json`) : tant qu'elle n'existe pas, le module affiche ses
 * étapes écrites seules, sans emplacement vide.
 */

/** Identifiants des vidéos tutos (= nom du fichier rendu dans other/donoor-tutos/out). */
export const TUTORIELS = [
  // Bien démarrer, votre association (other/donoor-tutos, scripts/rec-gestion.mjs,
  // Karim 2026-11-04) : l'inscription résumée pour tous les pays, ses
  // informations (Paramètres › Association), une deuxième association.
  "inscription",
  "reglages-association",
  "ajouter-une-association",
  "stripe",
  "paypal",
  "versements",
  // Lien de paiement, une vidéo par module (other/donoor-tutos, scripts/rec-lien.mjs) :
  // l'éditeur filmé avec son aperçu en direct, la page sur téléphone et sur
  // ordinateur, le don sur iPhone, le module pour site, le suivi, l'espace donateur.
  "lien-de-paiement-1",
  "lien-de-paiement-2",
  "lien-de-paiement-3",
  "lien-de-paiement-4",
  "lien-de-paiement-5",
  "lien-de-paiement-6",
  "lien-de-paiement-7",
  "lien-de-paiement-8",
  "lien-de-paiement-9",
  // Vidéo cagnotte découpée en 5 parties (Karim 2026-10-18).
  "cagnotte-0", // présentation de la formation
  "cagnotte-1",
  "cagnotte-2",
  "cagnotte-3",
  "cagnotte-4",
  "cagnotte-5",
  // QR codes découpés en 4 parties (other/donoor-tutos, scripts/rec-qr.mjs) :
  // créer, l'affiche, l'afficher et le scanner, suivre (scans, dons, écran TV).
  "qr-code-0", // présentation de la formation
  "qr-code-1",
  "qr-code-2",
  "qr-code-3",
  "qr-code-4",
  "qr-code-5", // remplacer la borne de don
  // Tap to Pay en 5 parties, une par module (other/donoor-tutos : motion design,
  // app iPhone filmée par scripts/rec-ios.mjs, dashboard par rec-taptopay.mjs).
  "tap-to-pay-1",
  "tap-to-pay-2",
  "tap-to-pay-3",
  "tap-to-pay-4",
  "tap-to-pay-5",
  // « Encaisser pour » : cagnotte, adhésion, billet, boutique, puis le QR code
  // des reçus scanné par le payeur (other/donoor-tutos, Karim 2026-10-25).
  "tap-to-pay-6",
  // Plus de 50 € : le donateur tape le code de sa carte sur le téléphone (Karim 2026-10-27).
  "tap-to-pay-7",
  // Billetterie en 6 parties (other/donoor-tutos, scripts/rec-billetterie.mjs + rec-checkin.mjs).
  "billetterie-1",
  "billetterie-2",
  "billetterie-3",
  "billetterie-4",
  "billetterie-5",
  "billetterie-6",
  // Paiement en plusieurs fois, un billet coûteux réglé en plusieurs prélèvements (Karim 2026-10-24).
  "billetterie-7",
  "adhesions",
  // Formation adhésion, une vidéo par module, tableau de bord + téléphone
  // (other/donoor-video, scripts/formations.sh).
  "adhesion-0", // présentation : du bulletin papier à l'adhésion en ligne (other/donoor-video)
  // Une vidéo par module, filmée comme la billetterie (other/donoor-tutos, rec-adhesion.mjs).
  "adhesion-1",
  "adhesion-2",
  "adhesion-3",
  "adhesion-4",
  "adhesion-5",
  "adhesion-6",
  "adhesion-7",
  "adhesion-8",
  // Paiement en plusieurs fois, une cotisation réglée en plusieurs prélèvements (Karim 2026-10-24).
  "adhesion-9",
  "boutique",
  // Une vidéo par module de la formation Boutique (other/donoor-tutos) :
  // dashboard, vitrine et achat sur iPhone, retrait scanné.
  "boutique-2",
  "boutique-3",
  "boutique-4",
  "boutique-5",
  // Paiement en plusieurs fois, une commande coûteuse réglée en plusieurs prélèvements (Karim 2026-10-24).
  "boutique-6",
  // Page internet, une vidéo par module (other/donoor-tutos, scripts/rec-page.mjs
  // + rec-app.mjs) : le dashboard avec son aperçu, puis la même chose dans
  // l'app iPhone ; la recherche Google qui mène à la page (vidéo 5).
  "page-internet-1",
  "page-internet-2",
  "page-internet-3",
  "page-internet-4",
  "page-internet-5",
  "emails-membres",
  "recus-fiscaux",
  // Formation reçus fiscaux (other/donoor-video, scripts/formations.sh) :
  // rendre les dons déductibles + ce que reçoit le donateur, en une vidéo
  // (Karim 2026-10-19 ; l'ancien module « Gérer le service » est supprimé).
  "recus-fiscaux-2",
  "equipe",
  // Inviter un membre (other/donoor-video) ; rôles et droits + vue du membre,
  // puis suivre l'équipe (other/donoor-tutos, dashboard + app iPhone).
  "equipe-1",
  "equipe-2",
  "equipe-3",
  "parrainage",
  "notifications",
  // Affichage TV en 5 parties (other/donoor-tutos, scripts/rec-tv.mjs) : créer
  // un écran, le personnaliser, l'ouvrir sur la TV, les dons en direct, gérer.
  "tv-1",
  "tv-2",
  "tv-3",
  "tv-4",
  "tv-5",
  // Webhooks découpés en 5 parties (other/donoor-tutos, scripts/rec-webhooks.mjs).
  "webhooks-0",
  "webhooks-1",
  "webhooks-2",
  "webhooks-3",
  "webhooks-4",
  "webhooks-5",
] as const;

export type TutorielId = (typeof TUTORIELS)[number];

export interface ModuleFormation {
  /** Identifiant stable du module : ancre de la page (#module-<id>) et clé des textes. */
  id: string;
  /** Vidéo tuto du module, si le module en a une. */
  tutoriel?: TutorielId;
}

/**
 * Pas de vidéo de présentation (Karim 2026-10-18 soir : « retire les vidéos
 * inutiles ») : « Le contenu de la formation » est le plan des modules, en
 * cartes qui expliquent chacune ce qu'on y apprend.
 */
export interface Formation {
  /** Adresse : /formations/<slug>. Ne JAMAIS la changer une fois en ligne (SEO). */
  slug: string;
  /** Icône Lucide (liens croisés, encart des pages produit). */
  icone: string;
  /**
   * Image de la carte et de l'en-tête (public/). Cagnotte, billetterie,
   * adhésion et boutique = captures des VRAIES pages publiques choisies par
   * Karim (cagnotte SUM, billetterie Bookin'events, adhésion Team Elite
   * Pétanque, boutique Petits Pigeons de Coeur ; public/images/formations/),
   * cadrées sur le haut de la page. `ecran` = capture de
   * l'interface, affichée entière sur fond gris ; `photo` = photo, recadrée.
   */
  image: {
    src: string;
    type: "photo" | "ecran";
    /** Cadrage d'une photo (object-position CSS). */
    position?: string;
  };
  /**
   * Prérequis de toutes les autres (créer le compte, connecter Stripe/PayPal) :
   * présenté à part, en bloc « Avant de commencer », pas en carte
   * fonctionnalité (Karim 2026-10-18).
   */
  prerequis?: boolean;
  /**
   * Documentation technique liée (adresse interne sans préfixe de langue) :
   * bouton à côté de « Commencer la formation » et entrée du sommaire.
   */
  documentation?: string;
  modules: ModuleFormation[];
  /** Fiches pratiques PDF (identifiants de `fiches/<langue>/<id>.json`). */
  fiches: string[];
}

export const FORMATIONS: readonly Formation[] = [
  {
    slug: "bien-demarrer",
    icone: "i-lucide-rocket",
    image: { src: "/assets/images/Admin.webp", type: "ecran" },
    prerequis: true,
    modules: [
      { id: "creer-son-compte", tutoriel: "inscription" },
      { id: "informations-association", tutoriel: "reglages-association" },
      { id: "ajouter-une-association", tutoriel: "ajouter-une-association" },
      { id: "connecter-stripe", tutoriel: "stripe" },
      { id: "connecter-paypal", tutoriel: "paypal" },
      { id: "versements", tutoriel: "versements" },
      { id: "equipe", tutoriel: "equipe" },
      { id: "notifications", tutoriel: "notifications" },
    ],
    fiches: ["pret-a-recevoir-vos-dons"],
  },
  {
    slug: "cagnotte",
    icone: "i-lucide-piggy-bank",
    image: {
      src: "/images/formations/cagnotte.webp",
      type: "photo",
      position: "50% 0%",
    },
    // Une partie de la vidéo par module (Karim 2026-10-18).
    modules: [
      { id: "creer-la-cagnotte", tutoriel: "cagnotte-1" },
      { id: "personnaliser-la-cagnotte", tutoriel: "cagnotte-2" },
      { id: "vue-donateur-stats-apercu", tutoriel: "cagnotte-3" },
      { id: "statuts-de-la-cagnotte", tutoriel: "cagnotte-4" },
      { id: "partager-la-cagnotte", tutoriel: "cagnotte-5" },
    ],
    fiches: ["preparer-sa-cagnotte", "calendrier-de-communication"],
  },
  {
    slug: "qr-codes",
    icone: "i-lucide-qr-code",
    image: { src: "/assets/qr/scan_qr_simulation.webp", type: "photo" },
    modules: [
      { id: "bases-sur-place", tutoriel: "qr-code-3" },
      { id: "qr-codes", tutoriel: "qr-code-1" },
      { id: "affiches", tutoriel: "qr-code-2" },
      { id: "ecran-en-direct", tutoriel: "qr-code-4" },
      { id: "bornes-de-don", tutoriel: "qr-code-5" },
    ],
    fiches: ["collecte-sur-place"],
  },
  {
    slug: "billetterie",
    icone: "i-lucide-ticket",
    image: {
      src: "/images/formations/billetterie.webp",
      type: "photo",
      position: "50% 0%",
    },
    modules: [
      { id: "creer-l-evenement", tutoriel: "billetterie-1" },
      { id: "billets-et-tarifs", tutoriel: "billetterie-2" },
      { id: "vendre-les-billets", tutoriel: "billetterie-3" },
      { id: "participants", tutoriel: "billetterie-4" },
      { id: "controle-des-entrees", tutoriel: "billetterie-5" },
      { id: "paiement-en-plusieurs-fois", tutoriel: "billetterie-7" },
      { id: "emails-aux-participants", tutoriel: "billetterie-6" },
    ],
    fiches: ["retroplanning-evenement", "le-jour-j"],
  },
  {
    slug: "adhesion",
    icone: "i-lucide-id-card",
    image: {
      src: "/images/formations/adhesion.webp",
      type: "photo",
      position: "50% 0%",
    },
    modules: [
      { id: "bases-adhesion", tutoriel: "adhesion-0" },
      { id: "creer-la-formule", tutoriel: "adhesion-1" },
      { id: "tarifs-et-options", tutoriel: "adhesion-2" },
      { id: "vendre-les-adhesions", tutoriel: "adhesion-3" },
      { id: "adherents", tutoriel: "adhesion-4" },
      { id: "valider-les-adhesions", tutoriel: "adhesion-5" },
      { id: "cartes-adherent", tutoriel: "adhesion-6" },
      { id: "renouvellement", tutoriel: "adhesion-7" },
      { id: "paiement-en-plusieurs-fois", tutoriel: "adhesion-9" },
      { id: "emails-aux-membres", tutoriel: "adhesion-8" },
    ],
    fiches: ["preparer-sa-campagne-d-adhesion"],
  },
  {
    slug: "lien-de-paiement",
    icone: "i-lucide-link",
    image: { src: "/assets/images/Phone.webp", type: "ecran" },
    modules: [
      { id: "lien-de-paiement", tutoriel: "lien-de-paiement-1" },
      { id: "personnaliser-la-page", tutoriel: "lien-de-paiement-2" },
      { id: "couleurs-et-boutons", tutoriel: "lien-de-paiement-3" },
      { id: "montants-d-impact", tutoriel: "lien-de-paiement-4" },
      { id: "objectif-et-options", tutoriel: "lien-de-paiement-5" },
      { id: "parcours-donateur", tutoriel: "lien-de-paiement-6" },
      { id: "don-sur-votre-site", tutoriel: "lien-de-paiement-7" },
      { id: "suivre-donateurs", tutoriel: "lien-de-paiement-8" },
      { id: "espace-donateur", tutoriel: "lien-de-paiement-9" },
    ],
    fiches: ["strategie-de-collecte", "fideliser-ses-donateurs"],
  },
  {
    slug: "boutique",
    icone: "i-lucide-shopping-bag",
    image: {
      src: "/images/formations/boutique.webp",
      type: "photo",
      position: "50% 0%",
    },
    modules: [
      { id: "creer-la-boutique", tutoriel: "boutique-2" },
      { id: "ajouter-des-produits", tutoriel: "boutique-3" },
      { id: "livraison-et-retrait", tutoriel: "boutique-4" },
      { id: "paiement-en-plusieurs-fois", tutoriel: "boutique-6" },
      { id: "gerer-les-commandes", tutoriel: "boutique-5" },
    ],
    fiches: ["lancer-sa-boutique"],
  },
  {
    slug: "tap-to-pay",
    icone: "i-lucide-smartphone-nfc",
    // Le vrai écran Tap to Pay de Donoor sur iPhone, carte approchée (Karim
    // 2026-10-18) — l'ancienne photo montrait un TPE, l'inverse du produit.
    // Version horizontale (téléphone entier centré sur son fond #f9f9f9) :
    // la photo verticale, recadrée, était « trop zoomée ».
    image: { src: "/images/formations/tap-to-pay.webp", type: "photo" },
    modules: [
      { id: "pourquoi-tap-to-pay", tutoriel: "tap-to-pay-1" },
      { id: "activer-tap-to-pay", tutoriel: "tap-to-pay-2" },
      { id: "encaisser", tutoriel: "tap-to-pay-3" },
      { id: "card-pin-over-50", tutoriel: "tap-to-pay-7" },
      { id: "collect-for-everything", tutoriel: "tap-to-pay-6" },
      { id: "recu-et-suivi", tutoriel: "tap-to-pay-4" },
      { id: "benevoles", tutoriel: "tap-to-pay-5" },
    ],
    fiches: ["tap-to-pay"],
  },
  {
    slug: "page-internet",
    icone: "i-lucide-globe",
    // La page d'une association fictive (Les Amis du Quartier) sur ordinateur
    // et sur iPhone, façon photo produit (1400 × 1400, remplit le cadre).
    // Fabriquée par other/donoor-tutos/scripts/image-formation-page.mjs.
    // La capture mobile seule rendait l'en-tête démesuré (Karim 2026-10-19).
    image: { src: "/images/formations/page-internet.webp", type: "photo", position: "50% 20%" },
    modules: [
      { id: "creer-la-page", tutoriel: "page-internet-1" },
      { id: "contenus", tutoriel: "page-internet-2" },
      { id: "actualites-et-galerie", tutoriel: "page-internet-3" },
      { id: "adresse-personnalisee", tutoriel: "page-internet-4" },
      { id: "etre-trouve-sur-google", tutoriel: "page-internet-5" },
    ],
    fiches: ["page-association-complete"],
  },
  {
    slug: "recus-fiscaux",
    icone: "i-lucide-file-check",
    image: { src: "/assets/fiscal/recu-fisc.webp", type: "ecran" },
    modules: [
      { id: "conditions" },
      { id: "activer-les-recus", tutoriel: "recus-fiscaux" },
      { id: "rendre-les-dons-deductibles", tutoriel: "recus-fiscaux-2" },
      { id: "obligations" },
    ],
    fiches: ["recus-fiscaux"],
  },
  {
    // Inviter les bénévoles et régler leurs droits (Karim 2026-10-18). Pas de
    // page produit dédiée : le lien produit mène au tableau de bord.
    slug: "equipe",
    icone: "i-lucide-users",
    image: { src: "/images/formations/equipe.webp", type: "ecran" },
    modules: [
      { id: "inviter-un-membre", tutoriel: "equipe-1" },
      { id: "roles-et-droits", tutoriel: "equipe-2" },
      { id: "suivre-l-equipe", tutoriel: "equipe-3" },
    ],
    fiches: ["equipe"],
  },
  {
    // Afficher les collectes en direct sur une TV (écrans TV, Karim 29/10).
    // Adresse en anglais : nouvelle page (règle du 25/10). Pas de page
    // produit dédiée : le lien produit mène au tableau de bord.
    slug: "tv-display",
    icone: "i-lucide-tv",
    image: { src: "/images/formations/tv-display.webp", type: "photo", position: "50% 55%" },
    modules: [
      { id: "create-a-screen", tutoriel: "tv-1" },
      { id: "customize-the-screen", tutoriel: "tv-2" },
      { id: "show-it-on-the-tv", tutoriel: "tv-3" },
      { id: "live-donations", tutoriel: "tv-4" },
      { id: "manage-your-screens", tutoriel: "tv-5" },
    ],
    fiches: ["tv-display-checklist"],
  },
  {
    // Relier Donoor aux outils de l'association. La documentation technique
    // complète reste à /developpeurs/webhooks (adresse inscrite dans chaque
    // message envoyé) ; la formation la relie, module par module.
    slug: "webhooks",
    icone: "i-lucide-webhook",
    documentation: "/developpeurs/webhooks",
    image: { src: "/images/formations/webhooks.webp", type: "ecran" },
    modules: [
      { id: "creer-un-webhook", tutoriel: "webhooks-1" },
      { id: "tester-et-suivre", tutoriel: "webhooks-2" },
      { id: "provenance-des-dons", tutoriel: "webhooks-3" },
      { id: "gerer-vos-webhooks", tutoriel: "webhooks-4" },
      { id: "guide-technique", tutoriel: "webhooks-5" },
    ],
    fiches: ["brancher-un-webhook"],
  },
];

export function trouverFormation(slug: string): Formation | undefined {
  return FORMATIONS.find((f) => f.slug === slug);
}
